import React from "react";
import "../css/pages/login.scss";
import { useNavigate } from "react-router-dom";
import { useLogin, useGetCurrentUser } from "../queries";
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import * as Yup from "yup";

interface Props {
  displayLoginState: boolean;
  closeLoginState: (e: boolean) => void;
}

interface iValues {
  username: string;
  password: string;
}

const validationSchema = Yup.object().shape({
  username: Yup.string()
    .trim()
    .required("(وارد کردن نام کاربری الزامی است)")
    .min(5, "(نام کاربری باید بیشتر از 4 حرف باشد)")
    .max(20, "(نام کاربری باید کمتر از 20 حرف باشد)"),
  password: Yup.string()
    .trim()
    .required("(وارد کردن رمز عبور الزامی است)")
    .min(9, "(رمز عبور باید بیشتر از 8 حرف باشد)")
    .max(20, "(رمز عبور باید کمتر از 20 حرف باشد)"),
});

const initialValues: iValues = {
  username: "",
  password: "",
};

const useLoginLogic = (
  displayLoginState: boolean,
  closeLoginState: (e: boolean) => void
) => {
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const history = useNavigate();

  // React Query hooks
  const { data: user, isLoading: userLoading } = useGetCurrentUser();
  const loginMutation = useLogin();

  React.useEffect(() => {
    if (userLoading) return;
    // If user is logged in, redirect to admin panel
    if (user?.id && displayLoginState) {
      history("/admin/adminpanel");
    }
  }, [user, displayLoginState, userLoading, history]);

  const handleClose = () => {
    closeLoginState(false);
  };

  const handleSubmit = async (
    values: iValues,
    { setSubmitting }: FormikHelpers<iValues>
  ) => {
    try {
      setSubmitting(true);
      setErrorMessage(""); // Clear previous errors

      loginMutation.mutate(values, {
        onSuccess: (data) => {
          // React Query handles token storage and user state automatically
          history("/");
        },
        onError: (error: any) => {
          setErrorMessage(error?.data?.error || "Login failed");
        },
        onSettled: () => {
          setSubmitting(false);
        },
      });
    } catch (error: any) {
      setErrorMessage(error?.data?.error || "Login failed");
      setSubmitting(false);
    }
  };

  return { handleClose, handleSubmit, errorMessage };
};

const Login: React.FC<Props> = ({ displayLoginState, closeLoginState }) => {
  const { handleClose, handleSubmit, errorMessage } = useLoginLogic(
    displayLoginState,
    closeLoginState
  );

  // Get loading state from the login mutation
  const loginMutation = useLogin();

  return (
    <div
      id="id01"
      className="modal"
      style={{ display: displayLoginState ? "block" : "none" }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form
            className="modal-content animate"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modalTitle">ورود به پنل ادمین</h3>
            <span
              onClick={handleClose}
              className="closeLoginForm"
              title="Close Modal"
            >
              &times;
            </span>
            <div className="imgcontainer"></div>
            <div className="container">
              <div className="labelErrorContainer">
                <label htmlFor="username">
                  <b>نام کاربری</b>
                </label>
                <div className="errorMessage">
                  <ErrorMessage name="username" />
                </div>
              </div>
              <Field
                type="text"
                placeholder="نام کاربری خود را وارد کنید"
                name="username"
              />
              <div className="labelErrorContainer">
                <label htmlFor="password">
                  <b>رمز عبور</b>
                </label>
                <div className="errorMessage">
                  <ErrorMessage name="password" />
                </div>
              </div>
              <Field
                type="password"
                placeholder="رمز عبور خود را وارد کنید"
                name="password"
                autoComplete="on"
              />

              <button
                type="submit"
                disabled={isSubmitting || loginMutation.isPending}
              >
                {isSubmitting || loginMutation.isPending
                  ? "در حال ورود..."
                  : "ورود به پنل ادمین"}
              </button>
              <div className="errorMessage" style={{ height: "13px" }}>
                {errorMessage ? errorMessage : <></>}
              </div>
            </div>
            <div className="container" style={{ backgroundColor: "f1f1f1" }}>
              <button type="button" onClick={handleClose} className="cancelbtn">
                انصراف
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Login;
